// ---------------------------------------------------------------------------
// MonthAdminActionsPane — aba "Ações" do grupo ADM no ProgressDrawerV3
// (leva 97).
// ---------------------------------------------------------------------------
// Espelha o ``AdminActionsPane`` do drawer de input, mas com escopo
// no MÊS inteiro. Visibilidade controlada por ``ProgressDrawerV3``
// (só empurra o item de rail quando ``userRole === "DEV"``). Mesmo
// gate vive no backend (``@requires('dev_only')`` em ``/reset-month/``).
//
// Ação principal: "Reiniciar modelagem do mês". Operação destrutiva,
// requer confirmação nativa (gate DEV ja restringe acesso — modal
// proprio seria peso visual desproporcional).
// ---------------------------------------------------------------------------

import React, { useState } from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";

import RestartAlt from "@material-ui/icons/Replay";

import useDrawerStyles from "../../drawer/styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import { resetMonthV3 } from "../../../../../services/inputsV3Service";

const formatSummary = (summary) => {
  if (!summary) return null;
  const parts = [];
  if (summary.inputs_reset) {
    parts.push(`${summary.inputs_reset} input(s) resetado(s)`);
  }
  if (summary.inputs_kept_auto_reuse) {
    parts.push(
      `${summary.inputs_kept_auto_reuse} input(s) auto-replicante(s) preservado(s)`,
    );
  }
  if (summary.str_notes_deleted) {
    parts.push(`${summary.str_notes_deleted} nota(s) de estrutura apagada(s)`);
  }
  if (summary.csl_notes_deleted) {
    parts.push(`${summary.csl_notes_deleted} nota(s) de cruzamento apagada(s)`);
  }
  if (summary.decisions_superseded) {
    parts.push(
      `${summary.decisions_superseded} pergunta(s) pendente(s) arquivada(s)`,
    );
  }
  if (summary.xlinks_reset) {
    parts.push(`${summary.xlinks_reset} cruzamento(s) zerado(s)`);
  }
  if (summary.blocks_marked_stale) {
    parts.push(
      `${summary.blocks_marked_stale} bloco(s) marcado(s) p/ recálculo`,
    );
  }
  return parts.length > 0 ? parts.join(" • ") : null;
};

const MonthAdminActionsPane = ({
  appName,
  year,
  month,
  userRole,
  onMonthReset,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  // Gate defensivo: o ``ProgressDrawerV3`` ja so empurra esta aba para
  // DEV; este check duplo recusa renderizar caso o pane seja montado
  // por algum estado forcado fora desse caminho.
  if (userRole !== "DEV") return null;

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleReset = async () => {
    if (!appName || !year || !month) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      "Tem certeza que deseja REINICIAR a modelagem deste mês? " +
        "Todos os inputs não auto-replicantes serão apagados, junto " +
        "com cruzamentos e notas. Esta ação não pode ser desfeita.",
    );
    if (!ok) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await resetMonthV3({ appName, year, month });
      const summary = formatSummary(res?.summary);
      setResult({
        ok: true,
        message:
          "Modelagem reiniciada com sucesso." + (summary ? ` ${summary}.` : ""),
      });
      if (typeof onMonthReset === "function") {
        onMonthReset();
      }
    } catch (err) {
      const code = err?.response?.data?.error_code || err?.message || "erro";
      setResult({
        ok: false,
        message: `Falha ao reiniciar modelagem (${code}).`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Ações administrativas</div>

        <div className={classes.admActionCard}>
          <div className={classes.admActionCardHeader}>
            <RestartAlt className={classes.admActionCardIcon} />
            <div className={classes.admActionCardTitle}>
              Reiniciar modelagem do mês
            </div>
          </div>
          <div className={classes.admActionCardDescription}>
            Reinicia a modelagem do mês selecionado. Exclui todos os arquivos
            enviados, exceto inputs marcados como{" "}
            <strong>&quot;reutilização automática&quot;</strong> (que se
            propagam automaticamente entre meses). Zera todos os cruzamentos e
            blocos.
          </div>
          <div className={classes.admActionCardFooter}>
            <button
              type="button"
              className={`${classes.btn} ${classes.btnDanger}`}
              onClick={handleReset}
              disabled={busy || !appName || !year || !month}
            >
              {busy ? (
                <ThreeDots
                  color={clientColor}
                  height="14px"
                  width="40px"
                  wrapperStyle={{ height: "14px", width: "40px" }}
                />
              ) : (
                <>
                  <RestartAlt />
                  Reiniciar mês
                </>
              )}
            </button>
          </div>
          {result && (
            <div
              className={
                result.ok
                  ? classes.admActionResultOk
                  : classes.admActionResultErr
              }
              role="status"
            >
              {result.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MonthAdminActionsPane.propTypes = {
  appName: PropTypes.string,
  year: PropTypes.number,
  month: PropTypes.number,
  userRole: PropTypes.string,
  onMonthReset: PropTypes.func,
};

export default MonthAdminActionsPane;
